import * as React from "react";

import {
  Activity,
  BellRing,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FilePlus2,
  GraduationCap,
  ShieldAlert,
  Users,
  type LucideIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FeedCard, type FeedTone } from "@/components/ui/feed-card";

import { type PoOrgActivityKind } from "./poOrgActivity";

import { PO_FEED_MAX_ITEMS, type PoFeedItem, type PoTeamFeedKind } from "./poFeed";

/** Per-variant copy for the Tier-1 team-blocker cards — the label carries the
 * blocker kind while the card itself always uses the shared team/people icon
 * (CHP-1010). `tone: "alert"` marks the destructive (breach) lane. */
const TEAM_KIND_COPY: Record<PoTeamFeedKind, { label: string; tone?: "alert" }> = {
  registration: { label: "Registration needs review" },
  extension: { label: "Extension needs review" },
  win: { label: "Closed-won needs review" },
  breach: { label: "Time Protection Triggered", tone: "alert" },
  "team-expiring": { label: "Team deal expiring soon" },
  // CHP-387 — pooled-payout 45-day-midpoint first-meeting-reward nudge.
  "meeting-reward-reminder": { label: "First meeting reward status" },
};

/** CHP-267 org-activity card copy — one entry per restricted event kind. The
 * label is the headline; `verb` completes the attributed sentence ("Bob BDM
 * <verb>"). Icons echo the related Tier-1 review cards so the feed reads
 * consistently. */
const ORG_ACTIVITY_COPY: Record<PoOrgActivityKind, { label: string; verb: string }> = {
  registration_submitted: { label: "Deal registered", verb: "submitted a registration" },
  registration_approved: { label: "Registration approved", verb: "had a registration approved" },
  registration_declined: { label: "Registration declined", verb: "had a registration declined" },
  extension_requested: { label: "Extension requested", verb: "requested an extension" },
  extension_approved: { label: "Extension approved", verb: "had an extension approved" },
  extension_declined: { label: "Extension declined", verb: "had an extension declined" },
  closed_won_submitted: { label: "Closed-won submitted", verb: "sent a closed-won for approval" },
  closed_won_approved: { label: "Closed-won approved", verb: "had a closed-won approved" },
  closed_won_declined: { label: "Closed-won declined", verb: "had a closed-won declined" },
  spif_earned: { label: "SPIF earned", verb: "earned a SPIF" },
};

/**
 * N4: the Tier-1 "team deal expiring" card surfaces an urgency COUNTDOWN, not an
 * absolute date — a coach scans the feed for what needs them soonest. Mirrors
 * the app's close-date convention (ExpiryNotificationCard / DealsDataGrid):
 * whole days, ceil-rounded, floored at 0 so an overdue live deal reads "today".
 */
function expiryCountdown(closeDate: string, now: Date): string {
  const diffMs = new Date(closeDate).getTime() - now.getTime();
  const days = Math.max(0, Math.ceil(diffMs / 86_400_000));
  if (days === 0) return "Expires today";
  if (days === 1) return "Expires in 1 day";
  return `Expires in ${days} days`;
}

export interface PoTodoFeedProps {
  /** Weight-sorted feed (output of buildPoFeed). */
  items: PoFeedItem[];
  /** Tier-1 team blocker → deep-link to the deal record. */
  onOpenTeamDeal: (dealId: string) => void;
  /** Tier-2 expiring → open the review/decision flow (deals dashboard). */
  onReviewExpiring: (dealId: string) => void;
  /** Tier-2 denied outcome → open the deal record. */
  onOpenOwnDeal: (dealId: string) => void;
  /** Tier-2 draft → resume the wizard. */
  onResumeDraft: (draftId: string) => void;
  /** Tier-3 enablement → open training. */
  onOpenTraining: () => void;
  /** Tier-2 expiring-card [X] — acknowledges expiry without opening the flow
   * (B2). Wired to the PO's own acknowledgeDealExpiry. */
  onDismissExpiring: (dealId: string) => void;
  /** Tier-2 draft-card [X] — hides the resume card (draft_dismissed_at; B2). */
  onDismissDraft: (dealId: string) => void;
  /** Tier-2 outcome-card [X] — hides the outcome card (outcome_dismissed_at; B2). */
  onDismissOutcome: (dealId: string) => void;
  /** CHP-387: pooled-payout midpoint card "Remind me in 14 days" — snoozes the
   * first-meeting-reward reminder (snoozeMeetingRewardReminder). */
  onSnoozeMeetingReward: (dealId: string) => void;
  /** CHP-387: pooled-payout midpoint card "Stop reminders" — stops the
   * first-meeting-reward reminder for this deal (stopMeetingRewardReminder). */
  onStopMeetingReward: (dealId: string) => void;
  /** CHP-804: a feed-source query failed — render an error/retry state instead
   * of the positive "All clear" empty state (which reads as "you're caught up"
   * when data actually failed). */
  feedError?: boolean;
  /** CHP-804: retry the failed feed queries. */
  onRetryFeed?: () => void;
  /** Reference clock for countdown labels; defaults to `new Date()`. */
  now?: Date;
}

/**
 * The Program Owner's weighted "player-coach" to-do feed.
 *
 * Renders the weight-sorted feed as shared FeedCard cards (team blockers,
 * personal actions, enablement), capped at PO_FEED_MAX_ITEMS with the training
 * card pinned above the cap. Every card's primary action deep-links:
 * team blockers to the deal record (TODO(followup #20) — the Team Activity Hub
 * messaging surface is not built, so Tier-1 cards open the deal/member record
 * instead), personal actions to the wizard / deals dashboard, enablement to
 * training. Always renders (no early null) — when the feed is empty it shows a
 * positive "all clear" state, which is itself a coaching signal.
 */
export function PoTodoFeed({
  items,
  onOpenTeamDeal,
  onReviewExpiring,
  onOpenOwnDeal,
  onResumeDraft,
  onOpenTraining,
  onDismissExpiring,
  onDismissDraft,
  onDismissOutcome,
  onSnoozeMeetingReward,
  onStopMeetingReward,
  feedError = false,
  onRetryFeed,
  now = new Date(),
}: PoTodoFeedProps) {
  // review-r2 (98a13a7c): "The feed cards must be able to be closed - with an
  // 'x' same as all other feed cards." The Tier-2 personal cards already wire
  // their [X] to a server-backed dismiss mutation. Tier-1 (team blockers),
  // Tier-3 (training) and Tier-4 (org activity) have no PO-scoped server
  // dismiss (a team member's deal isn't the coach's to acknowledge), so their
  // [X] is a client-side session dismiss tracked here — the card hides for the
  // session and re-surfaces on reload while the underlying item still applies.
  const [sessionDismissed, setSessionDismissed] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const dismissForSession = React.useCallback((key: string) => {
    setSessionDismissed((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Stable per-item key for the session-dismiss set (Tier-4 org activity is
  // keyed by its own id; everything else by the deal/enablement id).
  const dismissKey = (item: PoFeedItem) =>
    item.tier === 4 ? `org-${item.id}` : `${item.tier}-${item.id}`;

  const liveItems = items.filter((item) => !sessionDismissed.has(dismissKey(item)));

  // CHP-1019: collapsed the feed shows the capped set; a centered expander
  // toggles the full list (mirrors the deals dashboard's DealFeedSidebar).
  const [expanded, setExpanded] = React.useState(false);

  // CHP-465: the Tier-3 enablement (training) card must ALWAYS be visible so a
  // partner with outstanding compulsory training is prompted in the feed.
  // CHP-1004 ruling superseded the old deliberate pinned-but-Tier-3 placement:
  // buildPoFeed now returns the training card FIRST (mirroring the BDM feed),
  // so the first-N slice always contains it — this pin-above-cap set is kept
  // as a belt-and-braces safety net should the ordering ever change.
  const visibleSet = new Set(liveItems.slice(0, PO_FEED_MAX_ITEMS));
  const trainingItem = liveItems.find((item) => item.tier === 3);
  if (trainingItem) visibleSet.add(trainingItem);
  const cappedVisible = liveItems.filter((item) => visibleSet.has(item));
  const visible = expanded ? liveItems : cappedVisible;
  // Hidden count reflects the COLLAPSED state so the toggle keeps rendering
  // (as "Show fewer") once expanded.
  const hiddenCount = liveItems.length - cappedVisible.length;

  return (
    <aside className="space-y-3" aria-label="Active to-do feed">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-muted-foreground">Your Feed</h2>
        <Badge variant="secondary" data-testid="po-feed-count">
          {liveItems.length}
        </Badge>
      </div>

      {feedError ? (
        // CHP-804: a failed feed query must not read as "all clear".
        <Card data-testid="po-feed-error">
          <CardContent className="flex flex-col items-start gap-2 p-4">
            <p className="text-sm font-medium text-destructive">Couldn&apos;t load your feed</p>
            <p className="text-sm text-muted-foreground">
              Something went wrong loading your team and personal actions. Please try again.
            </p>
            {onRetryFeed ? (
              <Button variant="outline" size="sm" onClick={onRetryFeed}>
                Retry
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : liveItems.length === 0 ? (
        <Card data-testid="po-feed-empty">
          <CardContent className="flex items-center gap-3 p-4">
            <CheckCircle2 className="h-5 w-5 text-success" aria-hidden />
            <p className="text-sm text-muted-foreground">
              All clear — no team blockers or personal actions need you right now. Nice work keeping
              the pipeline moving.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {visible.map((item) => {
            // CHP-387 — pooled-payout first-meeting-reward midpoint card. Its
            // footer carries two actions (snooze 14d / stop), not the generic
            // "View deal", so it's rendered ahead of the generic Tier-1 block.
            if (item.tier === 1 && item.kind === "meeting-reward-reminder") {
              const { deal } = item;
              // CHP-1010: team card → shared FeedCard, people icon, no pill.
              // Passive card (no whole-card onClick); its two footer actions
              // (snooze / stop) live in the action slot.
              return (
                <FeedCard
                  key={`team-${item.id}`}
                  rawTestId={`po-feed-meeting-reward-${item.id}`}
                  tone="info"
                  icon={Users}
                  eyebrow="First meeting reward status"
                  title={deal.client_name}
                  description={
                    <div className="space-y-1">
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {deal.deal_id_display ? <span>{deal.deal_id_display}</span> : null}
                        {deal.lead_bdm_name ? <span>{deal.lead_bdm_name}</span> : null}
                      </div>
                      <p>
                        This deal has reached the 45-day midpoint and the first meeting reward has
                        not been claimed. Please check with the owner regarding the meeting status.
                      </p>
                    </div>
                  }
                  onDismiss={() => dismissForSession(dismissKey(item))}
                  dismissLabel={`Dismiss first meeting reward status for ${deal.client_name}`}
                  action={
                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onSnoozeMeetingReward(item.id)}
                        data-testid={`po-feed-meeting-reward-snooze-${item.id}`}
                      >
                        <BellRing className="h-3.5 w-3.5" aria-hidden />
                        Remind me in 14 days
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onStopMeetingReward(item.id)}
                        data-testid={`po-feed-meeting-reward-stop-${item.id}`}
                      >
                        Stop reminders
                      </Button>
                    </div>
                  }
                />
              );
            }

            if (item.tier === 1) {
              const copy = TEAM_KIND_COPY[item.kind];
              const isAlert = copy.tone === "alert";
              const { deal } = item;
              // CHP-1010: team card → shared FeedCard with the people icon and
              // no origin pill; the per-kind label carries the blocker kind.
              // CHP-407: the whole card opens the deal record; the [View deal]
              // button stops propagation so it doesn't double-fire.
              const openTeamDeal = () => onOpenTeamDeal(item.id);
              return (
                <FeedCard
                  key={`team-${item.id}`}
                  rawTestId={`po-feed-team-${item.id}`}
                  tone={isAlert ? "red" : item.kind === "team-expiring" ? "amber" : "neutral"}
                  icon={Users}
                  eyebrow={copy.label}
                  title={deal.client_name}
                  description={
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {deal.deal_id_display ? <span>{deal.deal_id_display}</span> : null}
                      {deal.lead_bdm_name ? <span>{deal.lead_bdm_name}</span> : null}
                      {item.kind === "breach" ? (
                        // review-r2 (2251cbd8): POs/BDMs must never see
                        // references to auto-approval (that's CM/RA/RM-only
                        // language) — surface the outcome to check, not the
                        // mechanism behind it.
                        <span className="text-destructive">Review the latest outcome</span>
                      ) : item.kind === "team-expiring" ? (
                        deal.target_close_date ? (
                          <span className="text-warning">
                            {expiryCountdown(deal.target_close_date, now)}
                          </span>
                        ) : null
                      ) : (
                        // CHP-794: POs/BDMs must never see auto-approval / SLA
                        // language (that's CM/RA/RM-only). The team review
                        // blockers (registration / extension / win) show a
                        // neutral "Awaiting review" urgency instead of the
                        // manager feed's "Auto-approves in Xh" countdown.
                        <span className="text-warning">Awaiting review</span>
                      )}
                    </div>
                  }
                  onClick={openTeamDeal}
                  ariaLabel={`View deal for ${deal.client_name}`}
                  onDismiss={() => dismissForSession(dismissKey(item))}
                  dismissLabel={`Dismiss ${copy.label} for ${deal.client_name}`}
                  action={
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openTeamDeal();
                      }}
                      data-testid={`po-feed-open-team-${item.id}`}
                    >
                      View deal
                    </Button>
                  }
                />
              );
            }

            if (item.tier === 3) {
              // CHP-1009: converged onto the shared FeedCard so the PO
              // enablement card reads like the BDM training card (red lane). The
              // whole card opens training; the CTA stops propagation.
              return (
                <FeedCard
                  key="enablement"
                  rawTestId="po-feed-enablement"
                  tone="red"
                  icon={GraduationCap}
                  eyebrow="Enablement"
                  title={`${item.outstandingCount} compulsory module${
                    item.outstandingCount === 1 ? "" : "s"
                  } outstanding`}
                  onClick={onOpenTraining}
                  ariaLabel="Finish your outstanding training"
                  onDismiss={() => dismissForSession(dismissKey(item))}
                  dismissLabel="Dismiss outstanding training"
                  action={
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenTraining();
                      }}
                      data-testid="po-feed-open-training"
                    >
                      Complete training
                    </Button>
                  }
                />
              );
            }

            // Tier 4 — CHP-267 org-activity notification (informational). The
            // card names the event + the deal owner it's attributed to, and
            // deep-links to the deal record for the PO to trace it.
            if (item.tier === 4) {
              const { activity } = item;
              const copy = ORG_ACTIVITY_COPY[activity.kind];
              const owner = activity.attribution;
              const openActivityDeal = () => onOpenTeamDeal(activity.deal.id);
              // CHP-1009: converged onto FeedCard (info lane) so it reads like
              // the BDM feed cards; the whole card deep-links to the deal record
              // and the [View deal] button stops propagation.
              return (
                <FeedCard
                  key={`org-${item.id}`}
                  rawTestId={`po-feed-org-activity-${activity.deal.id}`}
                  tone="info"
                  icon={Activity}
                  eyebrow={copy.label}
                  title={activity.deal.client_name}
                  description={
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {activity.deal.deal_id_display ? (
                        <span>{activity.deal.deal_id_display}</span>
                      ) : null}
                      <span>{owner ? `${owner} ${copy.verb}` : `A team member ${copy.verb}`}</span>
                    </div>
                  }
                  onClick={openActivityDeal}
                  ariaLabel={`View deal for ${activity.deal.client_name}`}
                  onDismiss={() => dismissForSession(dismissKey(item))}
                  dismissLabel={`Dismiss ${copy.label} for ${activity.deal.client_name}`}
                  action={
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        openActivityDeal();
                      }}
                      data-testid={`po-feed-org-activity-open-${activity.deal.id}`}
                    >
                      View deal
                    </Button>
                  }
                />
              );
            }

            // Tier 2 — personal pipeline. CHP-1009: converged onto FeedCard so
            // the PO's own cards read like the BDM feed cards; the origin pill
            // ("Personal action") is dropped. The whole card triggers its
            // primary action; the [X] dismiss and CTA stop propagation.
            const { deal, kind } = item;
            const isDenied = kind === "outcome" && deal.deal_status === "denied";
            const personalCopy: { label: string; icon: LucideIcon; tone: FeedTone } =
              kind === "expiring"
                ? { label: "Deal expiring soon", icon: CalendarClock, tone: "coral" }
                : kind === "draft"
                  ? { label: "Resume your draft", icon: FilePlus2, tone: "neutral" }
                  : isDenied
                    ? { label: "Registration declined", icon: ShieldAlert, tone: "coral" }
                    : { label: "Deal approved", icon: CheckCircle2, tone: "success" };
            const cta =
              kind === "expiring"
                ? { text: "Review options", fn: () => onReviewExpiring(item.id) }
                : kind === "draft"
                  ? { text: "Resume form", fn: () => onResumeDraft(item.id) }
                  : { text: "View deal", fn: () => onOpenOwnDeal(item.id) };
            // B2: each personal kind gets a non-destructive [X] wired to the
            // matching dismiss mutation (the same ones the BDM feed uses).
            const onDismiss =
              kind === "expiring"
                ? () => onDismissExpiring(item.id)
                : kind === "draft"
                  ? () => onDismissDraft(item.id)
                  : () => onDismissOutcome(item.id);
            return (
              <FeedCard
                key={`personal-${kind}-${item.id}`}
                rawTestId={`po-feed-personal-${item.id}`}
                tone={personalCopy.tone}
                icon={personalCopy.icon}
                eyebrow={personalCopy.label}
                title={deal.client_name}
                description={deal.deal_id_display ?? undefined}
                onClick={cta.fn}
                ariaLabel={`${cta.text} for ${deal.client_name}`}
                onDismiss={onDismiss}
                dismissLabel={`Dismiss ${personalCopy.label} for ${deal.client_name}`}
                dismissTestId={`po-feed-personal-dismiss-${item.id}`}
                action={
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      cta.fn();
                    }}
                    data-testid={`po-feed-personal-cta-${item.id}`}
                  >
                    {cta.text}
                  </Button>
                }
              />
            );
          })}
          {hiddenCount > 0 ? (
            // CHP-1019: a working, centered expander (was a dead "+ N more"
            // <p>) matching DealFeedSidebar's "See N more cards" control.
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              data-testid="po-feed-expand-toggle"
              className="flex items-center justify-center gap-1 rounded-md py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                  Show fewer
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                  See {hiddenCount} more {hiddenCount === 1 ? "card" : "cards"}
                </>
              )}
            </button>
          ) : null}
        </div>
      )}
    </aside>
  );
}
